import { gql } from '@apollo/client';
import type { DriverNames, UUID } from '@cobliteam/common-types';
import type { BatchCreateDriverItemInput } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import { driverGraphQLAPIToDriver, driverGraphQLAPIToDriverNames, driverToSaveDriverGraphQLAPI, newDriverToAddDriverGraphQLAPI } from './APITransformer';
import type { DriverGraphQLAPI } from './APITypes';
import type { Driver, NewDriver } from './Types';

export class GraphQLDriversRequester {
  public getDrivers = (): Promise<Driver[]> =>
    graphQLRequester
      .query<{ drivers: DriverGraphQLAPI[] }>({
        query: gql`
          query drivers {
            drivers {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
              identifier
              identification_token {
                id
                alias
                tokenId
                type
                status
                isExternal
                fleetId
                driverId
              }
              cpf
              driver_license
              registry_id
            }
          }
        `,
      })
      .then((result) => result.drivers.map(driverGraphQLAPIToDriver));

  public getDriversWithVehicles = (): Promise<Driver[]> =>
    graphQLRequester
      .query<{ drivers: DriverGraphQLAPI[] }>({
        query: gql`
          query drivers {
            drivers {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
              identification_token {
                id
                alias
                tokenId
                type
                status
                isExternal
                fleetId
                driverId
              }
              cpf
              driver_license
              registry_id
              vehicle {
                licensePlate
              }
            }
          }
        `,
      })
      .then((result) => result.drivers.map(driverGraphQLAPIToDriver));

  public getDriverNames = (): Promise<DriverNames> =>
    graphQLRequester
      .query<{ drivers: DriverGraphQLAPI[] }>({
        query: gql`
          query drivers {
            drivers {
              id
              name
            }
          }
        `,
      })
      .then((result) => driverGraphQLAPIToDriverNames(result.drivers));

  public deleteDriver = (driverId: UUID): Promise<UUID> => {
    const driverToDelete = { id: driverId };
    return graphQLRequester
      .mutate({
        mutation: gql`
          mutation DeleteDriver($driverToDelete: DeleteDriver) {
            deleteDriver(input: $driverToDelete) {
              id
            }
          }
        `,
        variables: { driverToDelete },
      })
      .then(() => driverToDelete.id);
  };

  public saveDriver = (driver: Driver | NewDriver): Promise<Driver> => {
    if ('id' in driver && driver.id) {
      return graphQLRequester
        .mutate<{ updateDriver: DriverGraphQLAPI }>({
          mutation: gql`
            mutation UpdateDriver($driverToUpdate: UpdateDriver) {
              updateDriver(input: $driverToUpdate) {
                id
                name
                fleet_id
                assigned
                phone_number
                first_license_acquirement_date
                license_expiration_date
                license_category
                driver_code
                cpf
                driver_license
                registry_id
              }
            }
          `,
          variables: {
            driverToUpdate: driverToSaveDriverGraphQLAPI(driver),
          },
        })
        .then((graphQlResponse) => driverGraphQLAPIToDriver(graphQlResponse.updateDriver));
    }
    return graphQLRequester
      .mutate<{ addDriver: DriverGraphQLAPI }>({
        mutation: gql`
          mutation AddDriver($driverToAdd: AddDriver) {
            addDriver(input: $driverToAdd) {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
            }
          }
        `,
        variables: {
          driverToAdd: newDriverToAddDriverGraphQLAPI(driver),
        },
      })
      .then((graphQlResponse) => {
        if (!graphQlResponse?.addDriver) {
          throw new Error('Failed to add driver: No response data received');
        }
        return driverGraphQLAPIToDriver(graphQlResponse.addDriver);
      });
  };

  public createDriversBatch = (drivers: BatchCreateDriverItemInput[]): Promise<Driver[]> => {
    return graphQLRequester
      .mutate<{ batchCreateDrivers: DriverGraphQLAPI[] }>({
        mutation: gql`
          mutation BatchCreateDrivers($input: BatchCreateDriversInput!) {
            batchCreateDrivers(input: $input) {
              id
              name
              fleet_id
              phone_number
              cpf
              driver_license
              registry_id
              license_category
              first_license_acquirement_date
              license_expiration_date
            }
          }
        `,
        variables: {
          input: { drivers },
        },
      })
      .then((graphQlResponse) => graphQlResponse.batchCreateDrivers.map(driverGraphQLAPIToDriver));
  };
}

export default GraphQLDriversRequester;
